/**
 * 権限（ロール）定義と判定ヘルパー
 *
 * これまで permissionId の生値（1〜4）がフロント各所にベタ書きされていたため、
 * 単一の真実の源としてここに集約する。
 *
 * ロールは実質「2次元」で管理される:
 *   - permissionId : Permission enum（下記）
 *   - isSuperAdmin : boolean（オーナーをさらに「プラットフォーム管理者」と「会社管理者」に分ける）
 *
 * 各ヘルパーは従来の数値比較と「完全に等価」になるよう定義している
 * （undefined/null の扱いも含む）。挙動を変えずに置換できることを保証する。
 */

/** 権限ID（数値は既存DBの mst_permissions.id と一致） */
export enum Permission {
  /** オーナー（管理者）。isSuperAdmin により ①PF管理者 / ②会社管理者 に分かれる */
  Owner = 1,
  /** 拠点管理者 */
  BaseAdmin = 2,
  /** 拠点一般ユーザー */
  BaseUser = 3,
  /** 一般ユーザー（最下位） */
  General = 4,
}

type PermissionValue = number | null | undefined;
type RoleUser = { permissionId?: number | null; isSuperAdmin?: boolean | null } | null | undefined;

/** オーナー（permissionId === 1） */
export const isOwner = (p: PermissionValue): boolean => p === Permission.Owner;

/** オーナー以外（permissionId !== 1） */
export const isNotOwner = (p: PermissionValue): boolean => p !== Permission.Owner;

/** 一般ユーザー（permissionId === 4） */
export const isGeneralUser = (p: PermissionValue): boolean => p === Permission.General;

/** 一般ユーザー以外（permissionId !== 4）。undefined も「一般以外」扱いになる従来挙動を踏襲 */
export const isNotGeneralUser = (p: PermissionValue): boolean => p !== Permission.General;

/** 拠点レベル（拠点管理者 or 拠点一般ユーザー = 2 or 3） */
export const isBaseLevel = (p: PermissionValue): boolean =>
  p === Permission.BaseAdmin || p === Permission.BaseUser;

/** ユーザー管理が可能な権限（1,2,3 = 一般ユーザー以外の有効ロール）。旧 `[1,2,3].includes` / `< 4` に相当 */
export const canManageUsers = (p: PermissionValue): boolean =>
  p === Permission.Owner || p === Permission.BaseAdmin || p === Permission.BaseUser;

/** 何らかの有効な権限を持つ（1〜4）。旧 `[1,2,3,4].includes` に相当 */
export const hasAnyRole = (p: PermissionValue): boolean =>
  p === Permission.Owner ||
  p === Permission.BaseAdmin ||
  p === Permission.BaseUser ||
  p === Permission.General;

/**
 * ① プラットフォーム管理者（RakuLogi運営）: オーナー かつ スーパー管理者。
 * 全テナント横断。将来の6階層モデルの①に相当（既に IsSuperAdmin で分岐済み）。
 */
export const isPlatformAdmin = (u: RoleUser): boolean =>
  !!u && u.permissionId === Permission.Owner && u.isSuperAdmin === true;

/**
 * ② 会社管理者（購入企業のオーナー）: オーナー かつ 非スーパー管理者。
 * 自テナント内で全権。将来の6階層モデルの②に相当。
 */
export const isCompanyOwner = (u: RoleUser): boolean =>
  !!u && u.permissionId === Permission.Owner && u.isSuperAdmin !== true;
